import { tokenStore } from "./tokenStore";

const getApiBaseUrl = () => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) return envUrl;

  if (import.meta.env.PROD) {
    console.error(
      "[TickAndPass CRITICAL] VITE_API_URL environment variable is missing from production build! " +
        "Please configure VITE_API_URL in Vercel project settings under the Production scope and trigger a full redeploy.",
    );
    if (
      typeof window !== "undefined" &&
      window.location?.origin &&
      !window.location.hostname.includes("localhost") &&
      !window.location.hostname.includes("127.0.0.1")
    ) {
      return "";
    }
  }

  return "http://127.0.0.1:3000";
};

const API_BASE_URL = getApiBaseUrl();

async function getAuthHeaders() {
  const token = tokenStore.getAccessToken();
  if (!token) return { "Content-Type": "application/json" };
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

async function safeFetch(url, options) {
  try {
    const response = await fetch(url, options);
    const text = await response.text();
    let body;
    try {
      body = JSON.parse(text);
    } catch {
      body = {};
    }

    if (!response.ok) {
      const msg =
        body?.error?.message ||
        body?.message ||
        `Server error (${response.status})`;
      // The code lets a page react to a case (ERR_PAYER_PHONE_REQUIRED).
      throw Object.assign(new Error(msg), { code: body?.error?.code });
    }
    return body?.data !== undefined ? body.data : body;
  } catch (err) {
    if (err.name === "TypeError" && err.message.includes("Failed to fetch")) {
      const targetDisplay =
        API_BASE_URL ||
        (typeof window !== "undefined"
          ? window.location.origin
          : "http://127.0.0.1:3000");
      throw new Error(
        `Unable to connect to verification server (${targetDisplay}). Please check backend connection and VITE_API_URL environment configuration.`,
      );
    }
    throw err;
  }
}

export async function verifyPanApi({ pan, accountType, entityName }) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(`${API_BASE_URL}/api/verification/pan/verify`, {
    method: "POST",
    headers,
    body: JSON.stringify({ pan, accountType, entityName }),
  });
}

/**
 * Flow A: Verify Individual PAN ('P') with Aadhaar OTP & Fuzzy Name Match (>= 80%)
 */
export async function verifyPanIndividualApi({
  pan,
  aadhaarSessionId,
  aadhaarOtp,
}) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(`${API_BASE_URL}/api/verification/pan/individual/verify`, {
    method: "POST",
    headers,
    body: JSON.stringify({ pan, aadhaarSessionId, aadhaarOtp }),
  });
}

/**
 * Flow B Step 2: Verify Commercial PAN Officer Aadhaar OTP (UIDAI authentication)
 */
export async function verifyCommercialOfficerAadhaarApi({
  pan,
  aadhaarSessionId,
  aadhaarOtp,
}) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(
    `${API_BASE_URL}/api/verification/pan/commercial/officer-aadhaar`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({ pan, aadhaarSessionId, aadhaarOtp }),
    },
  );
}

/**
 * Flow B Step 3 (Tier 1): Send GST OTP for Commercial Entity Linkage
 */
export async function sendCommercialGstOtpApi({ pan, gstin, userName }) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(
    `${API_BASE_URL}/api/verification/pan/commercial/gst-otp/send`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({ pan, gstin, userName }),
    },
  );
}

/**
 * Flow B Step 3: Verify Business Authorization Linkage (GST-OTP, Domain Match, or Manual Review)
 */
export async function verifyCommercialLinkageApi({
  pan,
  officerAadhaarToken,
  email,
  websiteUrl,
  gstin,
  gstRefId,
  gstOtp,
}) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(`${API_BASE_URL}/api/verification/pan/commercial/linkage`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      pan,
      officerAadhaarToken,
      email,
      websiteUrl,
      gstin,
      gstRefId,
      gstOtp,
    }),
  });
}

/**
 * Send GST OTP to Primary Authorized Signatory on file with GSTN
 */
export async function sendGstOtpApi({ gstin, accountType, userName }) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(`${API_BASE_URL}/api/verification/gst/send-otp`, {
    method: "POST",
    headers,
    body: JSON.stringify({ gstin, accountType, userName }),
  });
}

/**
 * Verify GST OTP and retrieve legal business details & verification token
 */
export async function verifyGstOtpApi({ gstin, refId, otp, accountType }) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(`${API_BASE_URL}/api/verification/gst/verify-otp`, {
    method: "POST",
    headers,
    body: JSON.stringify({ gstin, refId, otp, accountType }),
  });
}

/**
 * Retrieve registered taxpayer details against a GSTIN via FincorpX
 */
export async function fetchGstinDetailsApi(gstin) {
  return safeFetch(
    `${API_BASE_URL}/api/verification/gstin/details?gstin=${encodeURIComponent(gstin)}`,
    {
      method: "GET",
    },
  );
}

/**
 * @deprecated Legacy GST verification -- server will respond with 410 Gone
 */
export async function verifyGstApi({ gstin, accountType, entityName }) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(`${API_BASE_URL}/api/verification/gst`, {
    method: "POST",
    headers,
    body: JSON.stringify({ gstin, accountType, entityName }),
  });
}

/**
 * @deprecated Legacy GSTIN verification -- server will respond with 410 Gone
 */
export async function verifyGstinApi({ gstin, accountType }) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(`${API_BASE_URL}/api/verification/gstin/verify`, {
    method: "POST",
    headers,
    body: JSON.stringify({ gstin, accountType }),
  });
}

export async function sendAadhaarOtpApi({ aadhaarNumber, consent }) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(`${API_BASE_URL}/api/verification/aadhaar/send-otp`, {
    method: "POST",
    headers,
    body: JSON.stringify({ aadhaarNumber, consent }),
  });
}

export async function generateAadhaarOtpApi({ aadhaarNumber, consent }) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(`${API_BASE_URL}/api/verification/aadhaar/otp`, {
    method: "POST",
    headers,
    body: JSON.stringify({ aadhaarNumber, consent }),
  });
}

export async function verifyAadhaarOtpApi({ sessionId, otp }) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(`${API_BASE_URL}/api/verification/aadhaar/verify-otp`, {
    method: "POST",
    headers,
    body: JSON.stringify({ sessionId, otp }),
  });
}

export async function verifyAadhaarApi({ ref_id, refId, sessionId, otp }) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(`${API_BASE_URL}/api/verification/aadhaar/verify`, {
    method: "POST",
    headers,
    body: JSON.stringify({ ref_id: ref_id || refId || sessionId, otp }),
  });
}

export async function registerProfessionalApi({
  verificationToken,
  email,
  password,
  fullName,
  phone,
}) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(`${API_BASE_URL}/api/verification/professional/register`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      verificationToken,
      email,
      password,
      fullName,
      phone,
    }),
  });
}

export async function registerOrgApi(data) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(`${API_BASE_URL}/api/verification/org/register`, {
    method: "POST",
    headers,
    body: JSON.stringify(data),
  });
}

export async function getVerificationStatusApi() {
  const headers = await getAuthHeaders();
  return safeFetch(`${API_BASE_URL}/api/verification/status`, {
    method: "GET",
    headers,
  });
}

export async function startDigilockerVerificationApi({
  aadhaarNumber,
  consent,
  redirectUrl,
}) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(
    `${API_BASE_URL}/api/verification/aadhaar/digilocker/start`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({ aadhaarNumber, consent, redirectUrl }),
    },
  );
}

export async function getDigilockerStatusApi(verificationId) {
  return safeFetch(
    `${API_BASE_URL}/api/verification/aadhaar/digilocker/${verificationId}/status`,
    {
      method: "GET",
    },
  );
}

export async function validateCouponApi({
  code,
  accountType,
  verificationToken,
  verifierType,
  verifierId,
}) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(`${API_BASE_URL}/api/payment/coupon/validate`, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify({
      code,
      accountType,
      verificationToken,
      verifierType,
      verifierId,
    }),
  });
}

export async function createSubscriptionOrderApi({
  verificationToken,
  accountType,
  gatewayProvider,
  payerPhone,
  couponCode,
}) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(`${API_BASE_URL}/api/payment/subscription/create-order`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      verificationToken,
      accountType,
      gatewayProvider,
      payerPhone,
      couponCode,
    }),
  });
}

/** Confirms a sign-up order by its id: { paid, accountType }. */
export async function confirmSubscriptionOrderApi(orderId) {
  return safeFetch(`${API_BASE_URL}/api/payment/subscription/confirm-order`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ orderId }),
  });
}

export async function verifySubscriptionPaymentApi({
  verificationToken,
  gatewayProvider = "cashfree",
  gatewayOrderId,
  gatewayPaymentId,
}) {
  const headers = { "Content-Type": "application/json" };
  return safeFetch(`${API_BASE_URL}/api/payment/subscription/verify`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      verificationToken,
      gatewayProvider,
      gatewayOrderId,
      gatewayPaymentId,
    }),
  });
}

export async function detectOrgDomainApi(email) {
  return safeFetch(
    `${API_BASE_URL}/api/organisations/detect-domain?email=${encodeURIComponent(email)}`,
    { method: "GET" },
  );
}

export async function createJoinRequestApi(organisationId, payload) {
  return safeFetch(
    `${API_BASE_URL}/api/organisation/${organisationId}/join-request`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

/**
 * Active branches of an organisation, for the PRE-AUTH join-request flow -- the
 * applicant has no account yet, so this uses the public endpoint rather than
 * the authenticated branch list. Only active branches of an approved
 * organisation come back; the server re-checks the branch really belongs to
 * this organisation when the join request is submitted.
 */
export async function fetchPublicOrgBranchesApi(organisationId) {
  return safeFetch(
    `${API_BASE_URL}/api/organisations/${organisationId}/public-branches`,
    { method: "GET" },
  );
}
